import React, { useEffect, useState, useCallback, useRef } from "react";
import "./admin_employees.css";
import "../AddWorkReport/work_report.css";
import { toast } from "react-toastify";
import {
  addEmployee,
  deleteEmployee,
  getAllEmployees,
  updateEmployee,
  updateEmployeeStatus,
} from "../../service/manager_service.js";
import DataTable from "../../components/custom_data_table.jsx";
import CustomLoadingindicator from "../../components/custom_loading.jsx";
import {

  Search,
  Eye,
  Edit,
  Trash2,
  Camera,
  X,
  User2,
  Mail,
  Phone,
  Briefcase,
  Layers,
  Calendar,
} from "lucide-react";

const AdminEmployees = () => {
  const [formData, setFormData] = useState({
    employeeid: 0,
    employeename: "",
    mobilenumber: "",
    email: "",
    password: "",
    confirmPassword: "",
    designation: "",
    joiningdate: "",
    dateofbirth: "",
    profile: null,
    profilePreview: null,
  });

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // For "View" Modal
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // For "Delete" Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [isUpdate, setIsUpdate] = useState(false);
  const formRef = useRef(null);

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllEmployees();
      if (res.status === "success") {
        setEmployees(res.data.map((e, index) => ({ ...e, sno: index + 1 })));
      }
    } catch (error) {
      toast.error(error.message || "Failed to fetch employees");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Image size should be less than 2MB");
        return;
      }
      setFormData((prev) => ({
        ...prev,
        profile: file,
        profilePreview: URL.createObjectURL(file),
      }));
    }
  };

  const handleReset = () => {
    setFormData({
      employeeid: 0,
      employeename: "",
      mobilenumber: "",
      email: "",
      password: "",
      confirmPassword: "",
      designation: "",
      joiningdate: "",
      dateofbirth: "",
      profile: null,
      profilePreview: null,
    });
    setIsUpdate(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation logic from Admin Project
    if (!formData.employeename.trim()) {
      toast.error("Please enter employee name");
      return;
    }

    if (!formData.mobilenumber.trim()) {
      toast.error("Please enter mobile number");
      return;
    } else if (!/^\d{10}$/.test(formData.mobilenumber)) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }

    if (!formData.email.trim()) {
      toast.error("Please enter email");
      return;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (!isUpdate) {
      if (!formData.password) {
        toast.error("Please enter password");
        return;
      }
      if (!formData.confirmPassword) {
        toast.error("Please confirm password");
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        toast.error("Passwords do not match");
        return;
      }
    }

    if (!formData.designation) {
      toast.error("Please enter designation");
      return;
    }

    if (!formData.joiningdate) {
      toast.error("Please select joining date");
      return;
    }

    if (!formData.dateofbirth) {
      toast.error("Please select date of birth");
      return;
    }

    setSubmitting(true);
    try {
      const data = new FormData();
      Object.keys(formData).forEach((key) => {
        if (key !== "profilePreview" && formData[key] !== null) {
          data.append(key, formData[key]);
        }
      });

      const res = isUpdate
        ? await updateEmployee(data)
        : await addEmployee(data);

      if (res.status === "success" || res.statusCode === 201 || res.data) {
        toast.success(
          isUpdate
            ? "Employee updated successfully"
            : "Employee added successfully",
        );
        handleReset();
        fetchEmployees();
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (id) => {
    setDeleteTargetId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTargetId || deleteLoading) return;

    setDeleteLoading(true);
    try {
      const res = await deleteEmployee({ employeeid: deleteTargetId });
      if (res.status === "success") {
        toast.success("Employee deleted successfully");
        fetchEmployees();
        setShowDeleteModal(false);
        setDeleteTargetId(null);
      }
    } catch (error) {
      toast.error(error.message || "Failed to delete employee");
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDelete = () => {
    if (deleteLoading) return;
    setShowDeleteModal(false);
    setDeleteTargetId(null);
  };

  const handleToggleStatus = async (row) => {
    const newStatus = row.status === "Active" ? "Inactive" : "Active";
    try {
      const res = await updateEmployeeStatus({
        employeeid: row.employeeid,
        status: newStatus,
      });
      if (res.status === "success") {
        toast.success(`Employee marked as ${newStatus}`);
        fetchEmployees();
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleEdit = (row) => {
    setFormData({
      ...row,
      profile: null,
      profilePreview: row.profile || null,
    });
    setIsUpdate(true);
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleView = (row) => {
    setSelectedEmployee(row);
    setShowViewModal(true);
  };

  const columns = [
    { key: "sno", label: "S.No" },
    {
      key: "actions",
      label: "Actions",
      render: (_, row) => (
        <div className="table-actions">
          <button
            className="action-btn view"
            title="View"
            onClick={() => handleView(row)}
          >
            <Eye size={18} color="#475569" />
          </button>
          <button
            className="action-btn delete"
            title="Delete"
            onClick={() => handleDelete(row.employeeid)}
          >
            <Trash2 size={18} color="#ef4444" />
          </button>
          <button
            className="action-btn edit"
            title="Edit"
            onClick={() => handleEdit(row)}
          >
            <Edit size={18} color="#059669" />
          </button>
        </div>
      ),
    },
    {
      key: "profile",
      label: "Profile",
      render: (val) => (
        <img
          src={val || "https://cdn-icons-png.flaticon.com/128/3135/3135715.png"}
          alt="Profile"
          className="profile-thumb"
          onError={(e) => {
            e.target.src =
              "https://cdn-icons-png.flaticon.com/128/3135/3135715.png";
          }}
        />
      ),
    },
    { key: "employeename", label: "Employee Name" },
    { key: "mobilenumber", label: "Mobile" },
    { key: "email", label: "Email" },
    { key: "designation", label: "Designation" },
    { key: "joiningdate", label: "Joining Date" },
    { key: "dateofbirth", label: "Date of Birth" },
    {
      key: "status",
      label: "Status",
      render: (val, row) => (
        <div className="status-toggle-container">
          <span className={`status-badge ${val.toLowerCase()}`}>{val}</span>
          <label className="switch">
            <input
              type="checkbox"
              checked={val === "Active"}
              onChange={() => handleToggleStatus(row)}
            />
            <span className="slider round"></span>
          </label>
        </div>
      ),
    },
  ];

  const filteredEmployees = employees.filter(
    (e) =>
      e.employeename.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.email.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  if (loading && employees.length === 0) return <CustomLoadingindicator />;

  return (
    <div className="add-report-container employees-container">
      <h4>Employee Master</h4>

      {/* Creation Form */}
      <div className="add-report-card" ref={formRef}>
        <div className="profile-upload-section">
          <div
            className="profile-preview-container"
            onClick={() => document.getElementById("profile-input").click()}
            title="Update Profile Photo"
          >
            <img
              src={
                formData.profilePreview ||
                "https://cdn-icons-png.flaticon.com/128/3135/3135715.png"
              }
              alt="Preview"
              onError={(e) => {
                e.target.src =
                  "https://cdn-icons-png.flaticon.com/128/3135/3135715.png";
              }}
            />
            <div className="upload-overlay">
              <Camera size={16} />
            </div>
          </div>
          <span className="upload-hint">Upload profile photo</span>
          <input
            type="file"
            id="profile-input"
            hidden
            onChange={handleImageChange}
            accept="image/*"
          />
        </div>

        <form className="add-report-form" onSubmit={handleSubmit}>
          <div className="report-form-group">
            <label>Full name *</label>
            <input
              type="text"
              name="employeename"
              value={formData.employeename}
              onChange={handleChange}
              required
              placeholder="Enter name"
            />
          </div>

          <div className="report-form-group">
            <label>Mobile number *</label>
            <input
              type="text"
              name="mobilenumber"
              value={formData.mobilenumber}
              onChange={handleChange}
              required
              placeholder="10-digit number"
              maxLength={10}
            />
          </div>

          <div className="report-form-group">
            <label>Email address *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="example@gamil.com"
            />
          </div>

          {!isUpdate && (
            <>
              <div className="report-form-group">
                <label>Password *</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="Min 6 characters"
                />
              </div>

              <div className="report-form-group">
                <label>Confirm password *</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  placeholder="Re-enter password"
                />
              </div>
            </>
          )}

          <div className="report-form-group">
            <label>Designation *</label>
            <input
              type="text"
              name="designation"
              value={formData.designation}
              onChange={handleChange}
              required
              placeholder="Job title"
            />
          </div>

          <div className="report-form-group">
            <label>Joining date *</label>
            <input
              type="date"
              name="joiningdate"
              value={formData.joiningdate}
              onChange={handleChange}
              required
            />
          </div>

          <div className="report-form-group">
            <label>Date of birth *</label>
            <input
              type="date"
              name="dateofbirth"
              value={formData.dateofbirth}
              onChange={handleChange}
              required
            />
          </div>

          <div className="submit-buttons report-form-group-full">
            <button type="submit" className="apply-btn" disabled={submitting}>
              {submitting
                ? "Processing..."
                : isUpdate
                  ? "Update Employee"
                  : "Add Employee"}
            </button>
            <button type="button" className="reset-btn" onClick={handleReset}>
              Reset
            </button>
          </div>
        </form>
      </div>

      {/* List Section */}
      <div className="admin-header-section">

        <div className="search-wrapper">
          <Search size={18} color="#64748b" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredEmployees}
        loading={loading}
        pageSize={10}
      />

      {/* View Detail Modal */}
      {showViewModal && selectedEmployee && (
        <div className="premium-dialog-overlay" onClick={() => setShowViewModal(false)}>
          <div className="premium-view-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-premium">
              <div className="header-title-group">
                <div className="header-icon-badge">
                  <User2 size={20} />
                </div>
                <h5>Employee Profile Details</h5>
              </div>
              <button className="modal-close-circle" onClick={() => setShowViewModal(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="modal-body-scrollable">
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  paddingBottom: "10px",
                }}
              >
                <div style={{ position: "relative" }}>
                  <img
                    src={selectedEmployee.profile || "https://cdn-icons-png.flaticon.com/128/3135/3135715.png"}
                    alt="Profile"
                    onError={(e) => { e.target.src = "https://cdn-icons-png.flaticon.com/128/3135/3135715.png"; }}
                    style={{
                      width: "110px",
                      height: "110px",
                      borderRadius: "50%",
                      border: "4px solid #fff",
                      boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
                      objectFit: "cover",
                    }}
                  />
                  <div style={{
                    position: "absolute",
                    bottom: "5px",
                    right: "5px",
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    border: "3px solid #fff",
                    background: selectedEmployee.status?.toLowerCase() === "active" ? "#16a34a" : "#f59e0b",
                    boxShadow: "0 2px 5px rgba(0,0,0,0.2)"
                  }}></div>
                </div>
              </div>

              <div className="detail-section-card">
                <div className="detail-row-item">
                  <div className="detail-label-icon">
                    <User2 size={14} />
                    <span>Full Name</span>
                  </div>
                  <div className="detail-value-text">{selectedEmployee.employeename}</div>
                </div>

                <div className="detail-grid-layout">
                  <div className="detail-row-item">
                    <div className="detail-label-icon">
                      <Mail size={14} />
                      <span>Email Address</span>
                    </div>
                    <div className="detail-value-text" style={{ fontSize: "14px", overflowWrap: "anywhere" }}>{selectedEmployee.email}</div>
                  </div>
                  <div className="detail-row-item">
                    <div className="detail-label-icon">
                      <Phone size={14} />
                      <span>Mobile Number</span>
                    </div>
                    <div className="detail-value-text">{selectedEmployee.mobilenumber}</div>
                  </div>
                </div>

                <div className="detail-grid-layout" style={{ borderTop: "none", paddingTop: 0 }}>
                  <div className="detail-row-item">
                    <div className="detail-label-icon">
                      <Briefcase size={14} />
                      <span>Designation</span>
                    </div>
                    <div className="detail-value-text">{selectedEmployee.designation}</div>
                  </div>
                  <div className="detail-row-item">
                    <div className="detail-label-icon">
                      <Calendar size={14} />
                      <span>Joining Date</span>
                    </div>
                    <div className="detail-value-text">{selectedEmployee.joiningdate}</div>
                  </div>
                </div>

                <div className="detail-row-item">
                  <div className="detail-label-icon">
                    <Layers size={14} />
                    <span>Employment Status</span>
                  </div>
                  <div className={`status-pill-badge ${selectedEmployee.status?.toLowerCase() === "active" ? "active-user" : "pending"}`}>
                    <span className="indicator-dot"></span>
                    {selectedEmployee.status}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer-wrapper">
              <button className="modal-action-btn" onClick={() => setShowViewModal(false)}>
                Close Profile
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" style={{ backdropFilter: "blur(3px)" }}>
          <div className="confirm-modal-card">
            <div className="confirm-modal-icon">🗑️</div>
            <h3>Delete Employee</h3>
            <p>
              Are you sure you want to remove this employee? This action cannot
              be undone.
            </p>
            <div className="confirm-modal-buttons">
              <button
                className="cancel-btn"
                onClick={cancelDelete}
                disabled={deleteLoading}
                style={{ cursor: deleteLoading ? "not-allowed" : "pointer" }}
              >
                Cancel
              </button>
              <button
                className="delete-btn"
                onClick={confirmDelete}
                disabled={deleteLoading}
                style={{ cursor: deleteLoading ? "not-allowed" : "pointer" }}
              >
                {deleteLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ height: "50px" }}></div>
    </div>
  );
};

export default AdminEmployees;
